import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { loadRazorpay } from "@/lib/razorpay";
import { toast } from "sonner";
import { Check, Lightning, Buildings, CreditCard, Sparkle, Minus, Plus, WhatsappLogo, Info, ShareNetwork, CheckCircle } from "@phosphor-icons/react";

const fmtMoney = (n, sym = "\u20b9", locale = "en-IN") => `${sym}${Number(n).toLocaleString(locale)}`;

// Pick a sensible default currency: a saved manual choice wins; otherwise India → INR,
// everyone else → USD (based on the browser timezone / locale). No network call.
const detectCurrency = () => {
    try {
        const saved = localStorage.getItem("billing_currency");
        if (saved === "INR" || saved === "USD") return saved;
        const tz = (Intl.DateTimeFormat().resolvedOptions().timeZone || "").toLowerCase();
        const lang = (navigator.language || "").toLowerCase();
        const india = tz.includes("kolkata") || tz.includes("calcutta") || lang.endsWith("-in");
        return india ? "INR" : "USD";
    } catch (_e) { return "INR"; }
};

export default function Billing() {
    const { user, refresh } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState("");
    const [cycle, setCycle] = useState("monthly");
    const [currency, setCurrency] = useState(detectCurrency);
    const chooseCurrency = (c) => { setCurrency(c); try { localStorage.setItem("billing_currency", c); } catch (_e) { /* ignore */ } };
    const [seatMap, setSeatMap] = useState({});
    const [company, setCompany] = useState({ company_name: "", gstin: "", billing_email: "", address_line1: "", city: "", state: "", postal_code: "", country: "India" });
    const [savingCompany, setSavingCompany] = useState(false);
    const [invoices, setInvoices] = useState([]);
    const [previews, setPreviews] = useState({});
    const [dgOpen, setDgOpen] = useState(false);
    const [dgPlan, setDgPlan] = useState("");
    const [dgSeats, setDgSeats] = useState(0);
    const [dgBusy, setDgBusy] = useState(false);
    const GST = data?.gst_rate ?? 0.18;
    const withGst = (n) => (n == null ? null : Math.round(n * (1 + GST)));
    const sym = data?.currency_symbol || (currency === "USD" ? "$" : "\u20b9");
    const locale = currency === "USD" ? "en-US" : "en-IN";
    const fmt = (n) => fmtMoney(n, sym, locale);
    const discPct = Math.round((data?.yearly_discount ?? 0.1) * 100);

    const load = async () => {
        try {
            const { data } = await api.get("/billing", { params: { currency } });
            setData(data);
            setSeatMap((m) => {
                const next = { ...m };
                const active = data.subscription_status === "active" && data.seats;
                data.plans.forEach((p) => {
                    if (next[p.id] == null) {
                        if (active) {
                            const lo = p.min_seats || 3, hi = p.max_seats || 999;
                            next[p.id] = Math.max(lo, Math.min(hi, data.seats));
                        } else {
                            next[p.id] = p.min_seats || 3;
                        }
                    }
                });
                return next;
            });
            const [c, iv] = await Promise.all([api.get("/billing/company"), api.get("/billing/invoices")]);
            if (c.data && Object.keys(c.data).length) setCompany((prev) => ({ ...prev, ...c.data }));
            setInvoices(iv.data || []);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to load billing");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, [currency]);

    // Scroll to the calling plans when arriving from a "Add Call Center" upsell (#calling-plans).
    useEffect(() => {
        if (!loading && data && window.location.hash === "#calling-plans") {
            const el = document.getElementById("calling-plans");
            if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
        }
    }, [loading, data]);

    // Prorated upgrade previews (only when there's an active paid subscription; INR only).
    useEffect(() => {
        if (!data || data.subscription_status !== "active" || currency !== "INR") { setPreviews({}); return; }
        let cancelled = false;
        (async () => {
            const entries = {};
            for (const p of data.plans) {
                if (p.price_per_seat == null) continue;
                const isCur = p.id === data.plan;
                const curSeats = data.seats || 0;
                let sel = seatMap[p.id];
                if (sel == null) {
                    if (data.subscription_status === "active" && data.seats) {
                        const lo = p.min_seats || 3, hi = p.max_seats || 999;
                        sel = Math.max(lo, Math.min(hi, data.seats));
                    } else {
                        sel = p.min_seats || 3;
                    }
                }
                // Current plan: only preview when the customer is ADDING seats.
                if (isCur && sel <= curSeats) continue;
                try {
                    const { data: pv } = await api.get("/billing/upgrade-preview", {
                        params: { plan: p.id, cycle, seats: sel },
                    });
                    entries[p.id] = pv;
                } catch { /* ignore */ }
            }
            if (!cancelled) setPreviews(entries);
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, cycle, seatMap, currency]);

    const saveCompany = async () => {
        setSavingCompany(true);
        try {
            await api.put("/billing/company", company);
            toast.success("Company details saved — they'll appear on your invoices.");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
        } finally {
            setSavingCompany(false);
        }
    };

    const downloadInvoice = async (inv) => {
        try {
            const res = await api.get(`/billing/invoices/${inv.id}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `${inv.invoice_number.replace(/\//g, "-")}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("Could not download invoice");
        }
    };

    const isCurrentActivePlan = (p) => p.id === data?.plan && data?.subscription_status === "active";
    const currentSeatsFor = (p) => (isCurrentActivePlan(p) ? (data?.seats || 0) : 0);
    // Floor: you can't remove seats mid-cycle on your current plan.
    const loFor = (p) => (isCurrentActivePlan(p) ? Math.max(p.min_seats || 3, data?.seats || 0) : (p.min_seats || 3));
    const seatsFor = (p) => {
        if (seatMap[p.id] != null) return seatMap[p.id];
        if (data?.subscription_status === "active" && data?.seats) {
            const lo = p.min_seats || 3, hi = p.max_seats || 999;
            return Math.max(lo, Math.min(hi, data.seats));
        }
        return p.min_seats || 3;
    };
    const setSeats = (p, v) => {
        const lo = loFor(p);
        const hi = p.max_seats || 999;
        setSeatMap((m) => ({ ...m, [p.id]: Math.max(lo, Math.min(hi, v)) }));
    };
    const totalFor = (p) => {
        if (p.price_per_seat == null) return null;
        const s = seatsFor(p);
        return cycle === "yearly"
            ? Math.round(p.price_per_seat * s * 12 * (1 - (data?.yearly_discount ?? 0.1)))
            : p.price_per_seat * s;
    };
    // Per-user/month rate — discounted when billed yearly.
    const perSeatFor = (p) => {
        if (p.price_per_seat == null) return null;
        return cycle === "yearly"
            ? Math.round(p.price_per_seat * (1 - (data?.yearly_discount ?? 0.1)))
            : p.price_per_seat;
    };

    const PLAN_RANK = { essential: 1, professional: 2, custom: 3 };
    const startDowngrade = () => {
        const tp = data?.plan || "essential";
        setDgPlan(tp);
        setDgSeats(data?.seats || (data?.min_seats || 3));
        setDgOpen(true);
    };
    const scheduleDowngrade = async () => {
        setDgBusy(true);
        try {
            await api.post("/billing/schedule-downgrade", { plan: dgPlan, seats: Number(dgSeats) });
            toast.success("Change scheduled for your renewal date");
            setDgOpen(false);
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setDgBusy(false);
        }
    };
    const cancelDowngrade = async () => {
        setDgBusy(true);
        try {
            await api.delete("/billing/schedule-downgrade");
            toast.success("Scheduled change cancelled");
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setDgBusy(false);
        }
    };

    const buy = async (p) => {
        if (p.contact) {
            window.location.href = "mailto:support@inspiroworks.com?subject=Inspiro%20Connect%20Custom%20Plan";
            return;
        }
        setBusy(p.id);
        const ok = await loadRazorpay();
        if (!ok) { toast.error("Could not load the payment window. Check your connection."); setBusy(""); return; }
        try {
            const seats = seatsFor(p);
            const { data: order } = await api.post("/billing/checkout", { plan: p.id, cycle, seats, currency });
            const rzp = new window.Razorpay({
                key: order.razorpay_key_id,
                amount: order.amount,
                currency: order.currency,
                order_id: order.order_id,
                name: order.name,
                description: `${p.name} · ${seats} users · ${cycle}`,
                prefill: order.prefill,
                remember_customer: false,
                theme: { color: "#FF5A36" },
                handler: async (res) => {
                    try {
                        const { data: v } = await api.post("/billing/verify", {
                            razorpay_order_id: res.razorpay_order_id,
                            razorpay_payment_id: res.razorpay_payment_id,
                            razorpay_signature: res.razorpay_signature,
                        });
                        toast.success(v.message || "Payment successful");
                        await load();
                        try { await refresh?.(); } catch (_) {}
                    } catch (e) {
                        toast.error(formatApiError(e.response?.data?.detail) || "Payment verification failed");
                    }
                },
                modal: { ondismiss: () => setBusy("") },
            });
            rzp.on("payment.failed", () => { toast.error("Payment failed. Please try again."); setBusy(""); });
            rzp.open();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not start checkout");
        } finally {
            setBusy("");
        }
    };

    const buyWhatsApp = async (plan) => {
        setBusy(`wa-${plan}`);
        const ok = await loadRazorpay();
        if (!ok) { toast.error("Could not load the payment window. Check your connection."); setBusy(""); return; }
        try {
            const { data: order } = await api.post("/billing/whatsapp/checkout", { plan, cycle, currency });
            const label = `WhatsApp ${plan.charAt(0).toUpperCase() + plan.slice(1)}`;
            const rzp = new window.Razorpay({
                key: order.razorpay_key_id,
                amount: order.amount,
                currency: order.currency,
                order_id: order.order_id,
                name: order.name,
                description: `${label} · ${cycle}`,
                prefill: order.prefill,
                remember_customer: false,
                theme: { color: "#25D366" },
                handler: async (res) => {
                    try {
                        const { data: v } = await api.post("/billing/verify", {
                            razorpay_order_id: res.razorpay_order_id,
                            razorpay_payment_id: res.razorpay_payment_id,
                            razorpay_signature: res.razorpay_signature,
                        });
                        toast.success(v.message || "WhatsApp activated");
                        await load();
                        try { await refresh?.(); } catch (_) {}
                    } catch (e) {
                        toast.error(formatApiError(e.response?.data?.detail) || "Payment verification failed");
                    }
                },
                modal: { ondismiss: () => setBusy("") },
            });
            rzp.on("payment.failed", () => { toast.error("Payment failed. Please try again."); setBusy(""); });
            rzp.open();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not start checkout");
        } finally {
            setBusy("");
        }
    };

    const buyPack = async (packType, packId) => {
        setBusy(`pack-${packId}`);
        const ok = await loadRazorpay();
        if (!ok) { toast.error("Could not load the payment window. Check your connection."); setBusy(""); return; }
        try {
            const { data: order } = await api.post("/billing/whatsapp/pack/checkout", { pack_type: packType, pack_id: packId, cycle, currency });
            const rzp = new window.Razorpay({
                key: order.razorpay_key_id, amount: order.amount, currency: order.currency,
                order_id: order.order_id, name: order.name,
                description: `${packType === "message" ? "Message" : "Storage"} pack · ${cycle}`,
                prefill: order.prefill, remember_customer: false, theme: { color: "#25D366" },
                handler: async (res) => {
                    try {
                        const { data: v } = await api.post("/billing/verify", {
                            razorpay_order_id: res.razorpay_order_id,
                            razorpay_payment_id: res.razorpay_payment_id,
                            razorpay_signature: res.razorpay_signature,
                        });
                        toast.success(v.message || "Pack added");
                        await load(); try { await refresh?.(); } catch (_) {}
                    } catch (e) {
                        toast.error(formatApiError(e.response?.data?.detail) || "Payment verification failed");
                    }
                },
                modal: { ondismiss: () => setBusy("") },
            });
            rzp.on("payment.failed", () => { toast.error("Payment failed. Please try again."); setBusy(""); });
            rzp.open();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not start checkout");
        } finally { setBusy(""); }
    };


    const status = data?.subscription_status;
    const [billSection, setBillSection] = useState("calling");
    const secCls = (s) => (billSection === s ? "" : "hidden");
    const daysLeft = data?.trial_days_left;
    const fmtBillingDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

    return (
        <AppShell>
            <div className="mx-auto max-w-5xl px-6 py-10" data-testid="billing-page">
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">
                    <CreditCard size={14} weight="fill" /> Billing & Plans
                </div>
                <h1 className="mt-3 font-heading text-4xl font-bold tracking-tight">Choose the plan that fits your team.</h1>

                {loading ? (
                    <p className="mt-6 text-sm text-stone2">Loading…</p>
                ) : (
                    <>
                        <div data-testid="billing-status" className="mt-6 flex flex-wrap items-center gap-4 rounded-2xl border border-stone-200 bg-white p-5">
                            <div>
                                <div className="text-xs uppercase tracking-widest text-stone2">Current plan</div>
                                <div className="font-heading text-xl font-bold capitalize">{data.current_plan_meta?.name || data.plan}</div>
                            </div>
                            <div className="h-10 w-px bg-stone-200" />
                            <div>
                                <div className="text-xs uppercase tracking-widest text-stone2">Status</div>
                                {status === "trialing" ? (
                                    <div className="font-semibold text-amber-600" data-testid="billing-trial-status">
                                        Free trial — {daysLeft > 0 ? `${daysLeft} day${daysLeft === 1 ? "" : "s"} left` : "ends today"}
                                    </div>
                                ) : status === "active" ? (
                                    <div className="font-semibold text-emerald-600">Active{data.billing_cycle ? ` · ${data.billing_cycle}` : ""}</div>
                                ) : (
                                    <div className="font-semibold text-red-600" data-testid="billing-expired-status">Trial ended — upgrade to continue</div>
                                )}
                            </div>
                            <div className="h-10 w-px bg-stone-200" />
                            <div>
                                <div className="text-xs uppercase tracking-widest text-stone2">Users</div>
                                <div className="font-semibold">{data.user_count}{data.user_cap != null ? ` / ${data.user_cap}` : " / ∞"}</div>
                            </div>
                            <div className="h-10 w-px bg-stone-200" />
                            <div>
                                <div className="text-xs uppercase tracking-widest text-stone2">{status === "trialing" ? "Trial ends" : status === "active" ? "Renews on" : "Access until"}</div>
                                <div className="font-semibold" data-testid="billing-renewal-date">{fmtBillingDate(status === "trialing" ? data.trial_ends_at : data.paid_until)}{status === "active" && data.billing_cycle ? ` · billed ${data.billing_cycle}` : ""}</div>
                            </div>
                        </div>

                        {data.scheduled_change && (
                            <div data-testid="scheduled-change-banner" className="mt-4 flex flex-wrap items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4">
                                <div className="text-sm text-amber-900">
                                    <b>Scheduled change:</b> moving to <b className="capitalize">{data.scheduled_change.plan}</b> · {data.scheduled_change.seats} seat{data.scheduled_change.seats === 1 ? "" : "s"} on <b>{fmtBillingDate(data.scheduled_change.effective_at)}</b> (your renewal date). You keep your current plan &amp; seats until then — no mid-cycle change.
                                </div>
                                <button
                                    data-testid="cancel-scheduled-change"
                                    onClick={cancelDowngrade}
                                    disabled={dgBusy}
                                    className="ml-auto rounded-full border border-amber-300 px-4 py-1.5 text-xs font-semibold text-amber-800 transition-colors hover:bg-amber-100 disabled:opacity-50"
                                >Cancel scheduled change</button>
                            </div>
                        )}

                        {/* Billing cycle + currency toggles */}
                        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                            <div className="inline-flex items-center rounded-full border border-stone-200 bg-white p-1" data-testid="cycle-toggle">
                                <button
                                    data-testid="cycle-monthly"
                                    onClick={() => setCycle("monthly")}
                                    className={`rounded-full px-5 py-2 text-sm font-semibold transition-all ${cycle === "monthly" ? "bg-ink text-white" : "text-stone2 hover:text-ink"}`}
                                >Monthly</button>
                                <button
                                    data-testid="cycle-yearly"
                                    onClick={() => setCycle("yearly")}
                                    className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-all ${cycle === "yearly" ? "bg-ink text-white" : "text-stone2 hover:text-ink"}`}
                                >Yearly <span className="rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] uppercase tracking-wide text-white">Save {discPct}%</span></button>
                            </div>
                            <div className="inline-flex items-center rounded-full border border-stone-200 bg-white p-1" data-testid="currency-toggle">
                                {(data?.supported_currencies || ["INR", "USD"]).map((c) => (
                                    <button key={c} data-testid={`currency-${c.toLowerCase()}`} onClick={() => chooseCurrency(c)}
                                        className={`rounded-full px-4 py-2 text-sm font-semibold transition-all ${currency === c ? "bg-ink text-white" : "text-stone2 hover:text-ink"}`}>
                                        {c === "USD" ? "$ USD" : "\u20b9 INR"}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {data.razorpay_key_id && data.razorpay_key_id.startsWith("rzp_test") && (
                            <div data-testid="test-mode-hint" className="mt-6 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                                {currency === "USD" ? (
                                    <span><b>Test mode — paying in USD:</b> International (USD) checkout supports <b>cards only</b> (UPI, Netbanking &amp; wallets are INR-only rails). A domestic card can't pay a USD order — it needs an <b>international card</b> (e.g. <b>4111 1111 1111 1111</b>). Since this account currently blocks international cards, the USD charge won't complete until <b>International Payments is activated</b> in your Razorpay dashboard. Switch to <b>₹ INR</b> to test a full payment now.</span>
                                ) : (
                                    <span><b>Test mode — how to pay:</b> Easiest is <b>Netbanking</b> → pick any bank → click <b>Success</b> on the mock page. Or <b>UPI ID</b> <b>success@razorpay</b>, or domestic card <b>5104 0155 5555 5558</b> (any future expiry &amp; CVV). Avoid card <b>4111…</b> — it's treated as an international card, which your Razorpay account currently blocks.</span>
                                )}
                            </div>
                        )}
                        {currency === "USD" && (
                            <div data-testid="usd-invoice-contact" className="mt-3 text-center text-sm text-stone2">
                                Need custom USD pricing or an invoice for your finance team?{" "}
                                <a href="mailto:sales@inspiroworks.com" data-testid="usd-contact-sales-link" className="font-semibold text-ink underline decoration-emerald-400 underline-offset-2 hover:text-emerald-700">
                                    Contact sales@inspiroworks.com
                                </a>
                            </div>
                        )}

                        {/* Section switcher — keeps Calling / WhatsApp / Social tidy (one at a time) */}
                        <div className="mt-8 flex justify-center">
                            <div className="inline-flex flex-wrap items-center gap-1 rounded-full border border-stone-200 bg-white p-1" data-testid="billing-section-nav">
                                {[["calling", "Calling Plans", Lightning], ["whatsapp", "WhatsApp", WhatsappLogo], ["social", "Social", ShareNetwork]].map(([k, label, Icon]) => (
                                    <button key={k} data-testid={`bill-sec-${k}`} onClick={() => setBillSection(k)}
                                        className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-all ${billSection === k ? "bg-ink text-white" : "text-stone2 hover:text-ink"}`}>
                                        <Icon size={15} weight="fill" /> {label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Per-product entitlement strip — each product is billed independently */}
                        <div className="mt-5 grid gap-3 sm:grid-cols-3" data-testid="product-status-strip">
                            {[["calling", "Calling", Lightning], ["whatsapp", "WhatsApp", WhatsappLogo], ["social", "Social", ShareNetwork]].map(([k, label, Icon]) => {
                                const p = (user?.products || {})[k] || {};
                                const st = p.status || "locked";
                                const cfg = st === "active"
                                    ? { txt: "Active", cls: "border-emerald-200 bg-emerald-50 text-emerald-700", dot: "bg-emerald-500" }
                                    : st === "trialing"
                                        ? { txt: daysLeft > 0 ? `Trial · ${daysLeft}d left` : "Trial ends today", cls: "border-amber-200 bg-amber-50 text-amber-700", dot: "bg-amber-500" }
                                        : { txt: "Locked — subscribe", cls: "border-stone-200 bg-stone-50 text-stone-500", dot: "bg-stone-400" };
                                return (
                                    <button key={k} data-testid={`product-status-${k}`} onClick={() => setBillSection(k)}
                                        className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-all hover:-translate-y-0.5 ${cfg.cls}`}>
                                        <Icon size={18} weight="fill" className="shrink-0 opacity-80" />
                                        <div className="min-w-0">
                                            <div className="font-heading text-sm font-bold text-ink">{label}</div>
                                            <div className="flex items-center gap-1.5 text-xs font-semibold">
                                                <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} /> {cfg.txt}
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <SocialBillingSection currency={currency} cycle={cycle} cls={secCls("social")} />

                        {user?.standalone && data.whatsapp?.standalone_surcharge > 0 && (
                            <div data-testid="addon-reprice-note" className={`mx-auto mt-6 flex max-w-3xl items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm text-emerald-900 ${secCls("calling")}`}>
                                <Sparkle size={18} weight="fill" className="mt-0.5 shrink-0 text-emerald-600" />
                                <div>
                                    <b>Bundle &amp; save:</b> you're on WhatsApp <b>standalone</b> pricing today. Add any calling plan below and your WhatsApp automatically switches to the cheaper <b>add-on</b> rate — you save <b>{fmt(data.whatsapp.standalone_surcharge)}/mo</b> on WhatsApp, applied on your next renewal.
                                </div>
                            </div>
                        )}

                        <div id="calling-plans" className={`mt-8 grid grid-cols-1 gap-6 scroll-mt-24 md:grid-cols-3 ${secCls("calling")}`}>
                            {data.plans.map((p) => {
                                const seats = seatsFor(p);
                                const isCurrentPlan = p.id === data.plan && status === "active";
                                const planCurrentSeats = isCurrentPlan ? (data.seats || 0) : 0;
                                const addingSeats = isCurrentPlan && seats > planCurrentSeats;
                                const isCurrent = isCurrentPlan && !addingSeats;
                                const Icon = p.id === "professional" ? Lightning : p.id === "custom" ? Buildings : Sparkle;
                                const total = totalFor(p);
                                const seatFixed = (p.min_seats || 0) === (p.max_seats || 0);
                                const pv = previews[p.id];
                                const previewPending = addingSeats && !pv;
                                return (
                                    <div
                                        key={p.id}
                                        data-testid={`plan-card-${p.id}`}
                                        className={`relative flex flex-col rounded-2xl border bg-white p-6 transition-all ${p.highlight ? "border-brand shadow-lg shadow-brand/10 md:-translate-y-2" : "border-stone-200"}`}
                                    >
                                        {p.highlight && (
                                            <span className="absolute -top-3 left-6 rounded-full bg-brand px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white">Most popular</span>
                                        )}
                                        <div className="flex items-center gap-2">
                                            <Icon size={20} weight="fill" className={p.highlight ? "text-brand" : "text-ink"} />
                                            <h3 className="font-heading text-xl font-bold">{p.name}</h3>
                                        </div>
                                        <p className="mt-1 text-sm text-stone2">{p.tagline}</p>

                                        {p.price_per_seat != null ? (
                                            <>
                                                <div className="mt-4 flex items-end gap-1.5">
                                                    <span data-testid={`plan-perseat-${p.id}`} className="font-heading text-3xl font-bold">{fmt(perSeatFor(p))}</span>
                                                    <span className="mb-1 text-sm text-stone2">/user/mo</span>
                                                    {cycle === "yearly" && (
                                                        <span className="mb-1 text-sm text-stone-400 line-through">{fmt(p.price_per_seat)}</span>
                                                    )}
                                                </div>
                                                {cycle === "yearly" && (
                                                    <div className="mt-0.5 text-xs font-medium text-emerald-600">billed yearly · {discPct}% off</div>
                                                )}
                                                {/* Seat stepper */}
                                                <div className="mt-4">
                                                    <div className="mb-1.5 text-xs font-semibold uppercase tracking-widest text-stone2">{isCurrentPlan ? "Seats" : "Users / Seats"}</div>
                                                    <div className="flex items-center gap-3">
                                                        <div className="inline-flex items-center rounded-lg border border-stone-300">
                                                            <button
                                                                data-testid={`seat-dec-${p.id}`}
                                                                disabled={seatFixed || seats <= loFor(p)}
                                                                onClick={() => setSeats(p, seats - 1)}
                                                                className="grid h-9 w-9 place-items-center text-stone2 hover:text-ink disabled:opacity-30"
                                                            ><Minus size={14} weight="bold" /></button>
                                                            <span data-testid={`seat-count-${p.id}`} className="min-w-[2.5rem] text-center font-semibold">{seats}</span>
                                                            <button
                                                                data-testid={`seat-inc-${p.id}`}
                                                                disabled={seatFixed || seats >= (p.max_seats || 999)}
                                                                onClick={() => setSeats(p, seats + 1)}
                                                                className="grid h-9 w-9 place-items-center text-stone2 hover:text-ink disabled:opacity-30"
                                                            ><Plus size={14} weight="bold" /></button>
                                                        </div>
                                                        <span className="text-xs text-stone2">{seatFixed ? "fixed" : isCurrentPlan ? `${planCurrentSeats} now · up to ${p.max_seats}` : `${p.min_seats}–${p.max_seats} users`}</span>
                                                    </div>
                                                    {isCurrentPlan && (
                                                        <p data-testid={`seat-hint-${p.id}`} className="mt-1.5 text-xs text-stone2">
                                                            {addingSeats
                                                                ? `Adding ${seats - planCurrentSeats} seat${seats - planCurrentSeats > 1 ? "s" : ""} — you'll only pay the prorated amount for the rest of this cycle.`
                                                                : `You have ${planCurrentSeats} seat${planCurrentSeats === 1 ? "" : "s"}. Tap + to add more.`}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="mt-4 rounded-lg bg-stone-50 px-3 py-2.5 text-sm">
                                                    <div className="flex justify-between text-stone2"><span>Subtotal</span><span data-testid={`plan-total-${p.id}`}>{fmt(total)}</span></div>
                                                    {GST > 0 && (
                                                        <div className="flex justify-between text-stone2"><span>GST 18%</span><span>{fmt(Math.round(total * GST))}</span></div>
                                                    )}
                                                    <div className="mt-1 flex justify-between border-t border-stone-200 pt-1 font-heading text-lg font-bold">
                                                        <span>Total /{cycle === "yearly" ? "yr" : "mo"}</span>
                                                        <span data-testid={`plan-gross-${p.id}`}>{fmt(withGst(total))}</span>
                                                    </div>
                                                </div>
                                                {pv?.prorated && (
                                                    <div data-testid={`prorate-${p.id}`} className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs leading-relaxed text-emerald-800">
                                                        You'll pay <b>{fmt(pv.total)}</b> now <span className="text-emerald-600">(prorated, incl. GST)</span> — covering this change for the <b>{pv.days_left} day{pv.days_left === 1 ? "" : "s"}</b> left in your current cycle. Renews {pv.renews_on ? new Date(pv.renews_on).toLocaleDateString("en-IN") : ""} at {fmt(withGst(total))}/{cycle === "yearly" ? "yr" : "mo"}.
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="mt-4 flex items-end">
                                                <span className="font-heading text-3xl font-bold">Custom</span>
                                            </div>
                                        )}

                                        <ul className="mt-5 flex-1 space-y-2.5">
                                            {p.features.map((f, i) => (
                                                <li key={i} className="flex items-start gap-2 text-sm text-stone-700">
                                                    <Check size={16} weight="bold" className="mt-0.5 shrink-0 text-emerald-500" />
                                                    <span>{f}</span>
                                                </li>
                                            ))}
                                        </ul>
                                        <button
                                            data-testid={`buy-plan-${p.id}`}
                                            disabled={isCurrent || busy === p.id || previewPending}
                                            onClick={() => buy(p)}
                                            className={`mt-6 rounded-full py-3 font-heading text-sm font-semibold transition-all disabled:opacity-60 ${p.highlight ? "bg-brand text-white hover:-translate-y-0.5 hover:bg-brand-hover" : "border border-ink text-ink hover:bg-ink hover:text-white"}`}
                                        >
                                            {busy === p.id ? "Opening…" : previewPending ? "Calculating…" : isCurrent ? "Current plan" : p.contact ? "Contact sales" : addingSeats ? `Add ${seats - planCurrentSeats} seat${seats - planCurrentSeats > 1 ? "s" : ""} — Pay ${fmt(pv?.prorated ? pv.total : withGst(total))}` : pv?.prorated ? `Pay ${fmt(pv.total)} today` : `Pay ${fmt(withGst(total))}`}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>

                        {/* WhatsApp Messaging — volume tiers (Base / Growth / Enterprise) */}
                        {data.whatsapp && (() => {
                            const wa = data.whatsapp;
                            const onCC = wa.on_call_center_plan;   // add-on pricing if true, else standalone (+surcharge)
                            const waAmt = (t) => onCC
                                ? (cycle === "yearly" ? t.amount_addon_yearly : t.amount_addon_monthly)
                                : (cycle === "yearly" ? t.amount_standalone_yearly : t.amount_standalone_monthly);
                            const usePct = wa.eff_msg_limit ? Math.min(100, Math.round((wa.usage || 0) * 100 / wa.eff_msg_limit)) : 0;
                            const near = usePct >= 80;
                            const storPct = wa.storage_limit_bytes ? Math.min(100, Math.round((wa.storage_used_bytes || 0) * 100 / wa.storage_limit_bytes)) : 0;
                            const storNear = storPct >= 80;
                            return (
                                <div className={`mt-12 ${secCls("whatsapp")}`} data-testid="whatsapp-billing-section">
                                    <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">
                                        <WhatsappLogo size={14} weight="fill" className="text-[#25D366]" /> WhatsApp Messaging
                                    </div>
                                    <h2 className="mt-2 font-heading text-2xl font-bold tracking-tight">WhatsApp plans</h2>
                                    <p className="mt-1 text-sm text-stone2">Flat price per workspace — tiered by monthly message volume (broadcast + outbound).{cycle === "yearly" ? ` Billed yearly · ${discPct}% off.` : " Billed monthly."}{!onCC && " Standalone pricing shown (no call-center plan)."}</p>

                                    <div data-testid="wa-meta-fees-note" className="mt-4 flex items-start gap-2 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800">
                                        <Info size={17} weight="fill" className="mt-0.5 shrink-0 text-sky-500" />
                                        <span><b>Platform pricing only.</b> WhatsApp conversation charges are billed directly by Meta and are separate — Inspiro adds no markup or fees on top.</span>
                                    </div>

                                    {wa.override === "on" && (
                                        <div data-testid="wa-provider-enabled" className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                                            WhatsApp Messaging is <b>enabled for your workspace by your provider</b> — no payment needed.
                                        </div>
                                    )}
                                    {wa.override === "off" && (
                                        <div data-testid="wa-provider-disabled" className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                            WhatsApp Messaging is currently <b>disabled for your workspace</b>. Contact your provider to enable it.
                                        </div>
                                    )}
                                    {wa.enabled && (
                                        <div data-testid="wa-usage-meter" className="mt-4 space-y-3 rounded-xl border border-stone-200 bg-white px-4 py-3">
                                            <div>
                                                <div className="flex items-center justify-between text-sm">
                                                    <span className="font-semibold">Messages this month · <span className="capitalize">{wa.plan}</span> plan</span>
                                                    <span className={near ? "font-bold text-amber-600" : "text-stone2"}>{(wa.usage || 0).toLocaleString()} / {(wa.eff_msg_limit || wa.msg_limit || 0).toLocaleString()}{wa.msg_pack_msgs ? ` (incl. +${wa.msg_pack_msgs.toLocaleString()} pack)` : ""}</span>
                                                </div>
                                                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-stone-100">
                                                    <div className={`h-full rounded-full ${near ? "bg-amber-500" : "bg-[#25D366]"}`} style={{ width: `${usePct}%` }} />
                                                </div>
                                            </div>
                                            <div data-testid="wa-storage-meter">
                                                <div className="flex items-center justify-between text-sm">
                                                    <span className="font-semibold">WhatsApp media storage</span>
                                                    <span className={storNear ? "font-bold text-amber-600" : "text-stone2"}>{(wa.storage_used_bytes / 1073741824).toFixed(2)} GB / {(wa.storage_limit_bytes / 1073741824).toFixed(0)} GB{wa.storage_pack_gb ? ` (incl. +${wa.storage_pack_gb} GB pack)` : ""}</span>
                                                </div>
                                                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-stone-100">
                                                    <div className={`h-full rounded-full ${storNear ? "bg-amber-500" : "bg-sky-500"}`} style={{ width: `${storPct}%` }} />
                                                </div>
                                                {storNear && <div className="mt-1 text-xs text-amber-700">Storage almost full — add a storage pack below to keep sending media.</div>}
                                            </div>
                                            <div className="text-xs text-stone2">Media older than <b>{wa.retention_days} days</b> is auto-deleted (chat history is kept).{wa.override !== "on" && wa.paid_until ? ` · Renews ${fmtBillingDate(wa.paid_until)} · ${wa.mode === "standalone" ? "Standalone" : "Add-on"}` : ""}</div>
                                        </div>
                                    )}

                                    <div className="mt-5 grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4">
                                        {wa.tiers.map((t) => {
                                            const amt = waAmt(t);
                                            const gross = withGst(amt);
                                            const isActiveTier = wa.enabled && wa.plan === t.id && wa.override !== "on";
                                            const providerControlled = wa.override === "on" || wa.override === "off";
                                            const disabled = providerControlled || busy === `wa-${t.id}`;
                                            const featured = t.id === "growth";
                                            let cta = `Get ${t.name} — Pay ${fmt(gross)}`;
                                            if (busy === `wa-${t.id}`) cta = "Opening…";
                                            else if (wa.override === "off") cta = "Unavailable";
                                            else if (wa.override === "on") cta = "Enabled by provider";
                                            else if (isActiveTier) cta = `Renew — Pay ${fmt(gross)}`;
                                            return (
                                                <div key={t.id} data-testid={`wa-tier-${t.id}`} className={`relative flex flex-col rounded-2xl border bg-white p-6 transition-all ${featured ? "border-[#25D366] ring-1 ring-[#25D366]/30" : "border-stone-200"}`}>
                                                    {isActiveTier && (
                                                        <span className="absolute -top-3 left-6 rounded-full bg-emerald-500 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white">Active</span>
                                                    )}
                                                    {featured && !isActiveTier && (
                                                        <span className="absolute -top-3 right-6 rounded-full bg-[#25D366] px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white">Popular</span>
                                                    )}
                                                    <div className="flex items-center gap-2">
                                                        <WhatsappLogo size={20} weight="fill" className="text-[#25D366]" />
                                                        <h3 className="font-heading text-xl font-bold">{t.name}</h3>
                                                    </div>
                                                    <p className="mt-1 text-sm text-stone2">{t.tagline}</p>
                                                    <div className="mt-4 flex items-end gap-1.5">
                                                        <span data-testid={`wa-price-${t.id}`} className="font-heading text-3xl font-bold">{fmt(amt)}</span>
                                                        <span className="mb-1 text-sm text-stone2">/{cycle === "yearly" ? "yr" : "mo"}</span>
                                                    </div>
                                                    <div className="mt-0.5 text-xs text-stone2">{GST > 0 ? `+ GST 18% · ${fmt(gross)}/${cycle === "yearly" ? "yr" : "mo"} total` : `${fmt(gross)}/${cycle === "yearly" ? "yr" : "mo"} · no GST`}{!onCC ? " · standalone" : ""}</div>
                                                    <div className="mt-3 flex flex-wrap gap-2">
                                                        <span className="rounded-full bg-stone-100 px-2.5 py-1 text-xs font-semibold text-stone-700">{(t.msg_limit / 1000).toLocaleString()}k msgs/mo</span>
                                                        <span className="rounded-full bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-700">{t.storage_gb} GB media</span>
                                                        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${t.journeys ? "bg-violet-50 text-violet-700" : "bg-stone-100 text-stone-400"}`}>{t.journeys ? "Journeys ✓" : "No Journeys"}</span>
                                                        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${t.salesforce ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-400"}`}>{t.salesforce ? "Salesforce ✓" : "No Salesforce"}</span>
                                                        <span className="rounded-full bg-stone-100 px-2.5 py-1 text-xs font-semibold text-stone-600">{t.retention_days}-day retention</span>
                                                    </div>
                                                    <ul className="mt-5 flex-1 space-y-2.5">
                                                        {t.features.map((f, i) => (
                                                            <li key={i} className="flex items-start gap-2 text-sm text-stone-700">
                                                                <Check size={16} weight="bold" className="mt-0.5 shrink-0 text-[#25D366]" />
                                                                <span>{f}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                    <button
                                                        data-testid={`buy-wa-${t.id}`}
                                                        disabled={disabled}
                                                        onClick={() => buyWhatsApp(t.id)}
                                                        className="mt-6 rounded-full bg-[#25D366] py-3 font-heading text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-[#1eb658] disabled:translate-y-0 disabled:opacity-60"
                                                    >{cta}</button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {/* Add-on packs — top up messages / storage on any tier */}
                                    {wa.enabled && (
                                        <div className="mt-8" data-testid="wa-packs">
                                            <h3 className="font-heading text-lg font-bold">Add-on packs</h3>
                                            <p className="mt-1 text-sm text-stone2">Top up without changing tier — stacks on your plan for the {cycle === "yearly" ? "year" : "month"}, buy again anytime.</p>
                                            <div className="mt-4 grid gap-4 md:grid-cols-2">
                                                <div className="rounded-2xl border border-stone-200 bg-white p-5">
                                                    <div className="text-sm font-bold text-[#25D366]">Message packs</div>
                                                    <div className="mt-3 space-y-2">
                                                        {wa.message_packs.map((p) => (
                                                            <div key={p.id} data-testid={`msg-pack-${p.id}`} className="flex items-center justify-between rounded-xl border border-stone-100 px-3 py-2">
                                                                <span className="text-sm font-semibold">+{(p.msgs / 1000).toLocaleString()}k messages</span>
                                                                <button data-testid={`buy-msg-pack-${p.id}`} disabled={busy === `pack-${p.id}`} onClick={() => buyPack("message", p.id)}
                                                                    className="rounded-full bg-ink px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50">
                                                                    {busy === `pack-${p.id}` ? "Opening…" : `${fmt(cycle === "yearly" ? p.amount_yearly : p.amount_monthly)}/${cycle === "yearly" ? "yr" : "mo"}`}
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                                <div className="rounded-2xl border border-stone-200 bg-white p-5">
                                                    <div className="text-sm font-bold text-sky-600">Storage packs</div>
                                                    <div className="mt-3 space-y-2">
                                                        {wa.storage_packs.map((p) => (
                                                            <div key={p.id} data-testid={`storage-pack-${p.id}`} className="flex items-center justify-between rounded-xl border border-stone-100 px-3 py-2">
                                                                <span className="text-sm font-semibold">+{p.gb} GB media storage</span>
                                                                <button data-testid={`buy-storage-pack-${p.id}`} disabled={busy === `pack-${p.id}`} onClick={() => buyPack("storage", p.id)}
                                                                    className="rounded-full bg-ink px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50">
                                                                    {busy === `pack-${p.id}` ? "Opening…" : `${fmt(cycle === "yearly" ? p.amount_yearly : p.amount_monthly)}/${cycle === "yearly" ? "yr" : "mo"}`}
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {!onCC && (
                                        <p className="mt-3 text-center text-xs text-stone2">Standalone pricing includes a dedicated WhatsApp-only workspace (call-center/telephony disabled). Buy a call-center plan to switch to Add-on pricing.</p>
                                    )}
                                </div>
                            );
                        })()}

                        {status === "active" && (
                            <div className={`mt-8 rounded-2xl border border-stone-200 bg-white p-6 ${secCls("calling")}`} data-testid="downgrade-section">
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div>
                                        <h2 className="font-heading text-lg font-bold">Reduce plan or seats</h2>
                                        <p className="mt-1 text-sm text-stone2">Moving to a lower plan or fewer seats takes effect at your renewal ({fmtBillingDate(data.paid_until)}) — you keep everything until then, with no mid-cycle charge or refund.</p>
                                    </div>
                                    {!dgOpen && (
                                        <button data-testid="open-downgrade" onClick={startDowngrade} className="rounded-full border border-ink px-4 py-2 text-sm font-semibold text-ink transition-colors hover:bg-ink hover:text-white">Schedule a change</button>
                                    )}
                                </div>
                                {dgOpen && (
                                    <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
                                        <label className="text-xs font-semibold text-stone2">Plan
                                            <select
                                                data-testid="dg-plan"
                                                value={dgPlan}
                                                onChange={(e) => {
                                                    const np = e.target.value; setDgPlan(np);
                                                    const m = data.plans.find((x) => x.id === np);
                                                    if (m) { const hi = m.max_seats || 999, lo = m.min_seats || 3; setDgSeats((s) => Math.max(lo, Math.min(hi, Number(s) || lo))); }
                                                }}
                                                className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm text-ink outline-none focus:border-ink"
                                            >
                                                {data.plans.filter((x) => !x.contact && (PLAN_RANK[x.id] || 1) <= (PLAN_RANK[data.plan] || 1)).map((x) => (
                                                    <option key={x.id} value={x.id}>{x.name}</option>
                                                ))}
                                            </select>
                                        </label>
                                        <label className="text-xs font-semibold text-stone2">Seats
                                            <input data-testid="dg-seats" type="number" min="1" value={dgSeats} onChange={(e) => setDgSeats(e.target.value)} className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-ink" />
                                        </label>
                                        <div className="flex items-end gap-2">
                                            <button data-testid="dg-confirm" disabled={dgBusy || !((PLAN_RANK[dgPlan] || 1) < (PLAN_RANK[data.plan] || 1) || (dgPlan === data.plan && Number(dgSeats) < (data.seats || 0)))} onClick={scheduleDowngrade} className="rounded-full bg-ink px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{dgBusy ? "Scheduling…" : `Schedule for ${fmtBillingDate(data.paid_until)}`}</button>
                                            <button data-testid="dg-cancel-form" onClick={() => setDgOpen(false)} className="rounded-full border border-stone-300 px-4 py-2 text-sm font-semibold text-stone-600 transition-colors hover:bg-stone-100">Cancel</button>
                                        </div>
                                        <p className="text-xs text-stone2 sm:col-span-3">Tip: to upgrade or add seats, use the plan cards above — those apply immediately (prorated).</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Company details for GST invoices */}
                        <div className="mt-10 rounded-2xl border border-stone-200 bg-white p-6" data-testid="company-details-card">
                            <h2 className="font-heading text-lg font-bold">Company details for GST invoices</h2>
                            <p className="mt-1 text-sm text-stone2">These appear on the tax invoice generated after each payment.</p>
                            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                                {[
                                    ["company_name", "Registered company name", "Acme Pvt Ltd"],
                                    ["gstin", "GSTIN (optional)", "27AAAAA0000A1Z5"],
                                    ["billing_email", "Billing email", "accounts@acme.com"],
                                    ["address_line1", "Address", "12 MG Road"],
                                    ["city", "City", "Pune"],
                                    ["state", "State", "Maharashtra"],
                                    ["postal_code", "PIN code", "411001"],
                                    ["country", "Country", "India"],
                                ].map(([key, label, ph]) => (
                                    <div key={key}>
                                        <label className="text-xs font-semibold uppercase tracking-widest text-stone2">{label}</label>
                                        <input
                                            data-testid={`company-${key}`}
                                            value={company[key] || ""}
                                            onChange={(e) => setCompany({ ...company, [key]: e.target.value })}
                                            placeholder={ph}
                                            className="mt-1.5 w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm outline-none focus:border-ink"
                                        />
                                    </div>
                                ))}
                            </div>
                            <button
                                data-testid="save-company-btn"
                                onClick={saveCompany}
                                disabled={savingCompany}
                                className="mt-5 rounded-full bg-ink px-6 py-2.5 font-heading text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-stone-800 disabled:opacity-50"
                            >
                                {savingCompany ? "Saving…" : "Save company details"}
                            </button>
                        </div>

                        {/* Invoices */}
                        <div className="mt-8 rounded-2xl border border-stone-200 bg-white p-6" data-testid="invoices-card">
                            <h2 className="font-heading text-lg font-bold">Invoices</h2>
                            {invoices.length === 0 ? (
                                <p className="mt-2 text-sm text-stone2" data-testid="invoices-empty">No invoices yet. A GST invoice is generated automatically after each successful payment.</p>
                            ) : (
                                <div className="mt-4 overflow-hidden rounded-lg border border-stone-200">
                                    <table className="w-full text-sm">
                                        <thead className="bg-stone-50 text-left text-xs uppercase tracking-widest text-stone2">
                                            <tr>
                                                <th className="px-4 py-2.5">Invoice #</th>
                                                <th className="px-4 py-2.5">Date</th>
                                                <th className="px-4 py-2.5">Plan</th>
                                                <th className="px-4 py-2.5 text-right">Amount</th>
                                                <th className="px-4 py-2.5 text-right">PDF</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {invoices.map((iv) => (
                                                <tr key={iv.id} data-testid={`invoice-row-${iv.id}`} className="border-t border-stone-100">
                                                    <td className="px-4 py-3 font-medium">{iv.invoice_number}</td>
                                                    <td className="px-4 py-3 text-stone2">{new Date(iv.date).toLocaleDateString("en-IN")}</td>
                                                    <td className="px-4 py-3 capitalize">{iv.plan} · {iv.cycle}</td>
                                                    <td className="px-4 py-3 text-right">{fmt(iv.total)}</td>
                                                    <td className="px-4 py-3 text-right">
                                                        <button
                                                            data-testid={`download-invoice-${iv.id}`}
                                                            onClick={() => downloadInvoice(iv)}
                                                            className="rounded-lg border border-ink px-3 py-1.5 text-xs font-semibold text-ink transition-all hover:bg-ink hover:text-white"
                                                        >Download</button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </AppShell>
    );
}

function SocialBillingSection({ currency, cycle, cls }) {
    const [d, setD] = useState(null);
    const [busy, setBusy] = useState("");
    const load = () => api.get("/social-billing", { params: { currency } }).then(({ data }) => setD(data)).catch(() => {});
    useEffect(() => { load(); }, [currency]); // eslint-disable-line
    const ent = d?.entitlement;

    const startTrial = async () => {
        setBusy("trial");
        try { await api.post("/social-billing/trial"); toast.success("Social free trial started 🎉"); load(); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not start trial"); }
        finally { setBusy(""); }
    };
    const subscribe = async (tierId) => {
        setBusy(tierId);
        try {
            const ok = await loadRazorpay();
            if (!ok) { toast.error("Could not load payment window."); setBusy(""); return; }
            const { data: order } = await api.post("/social-billing/checkout", { tier: tierId, cycle, currency });
            const rzp = new window.Razorpay({
                key: order.razorpay_key_id, amount: order.amount, currency: order.currency,
                name: order.name, description: `Social ${tierId} (${cycle})`, order_id: order.order_id, prefill: order.prefill,
                handler: async (res) => {
                    try {
                        await api.post("/social-billing/verify", {
                            razorpay_order_id: res.razorpay_order_id, razorpay_payment_id: res.razorpay_payment_id,
                            razorpay_signature: res.razorpay_signature });
                        toast.success("Social add-on activated 🎉"); load();
                    } catch { toast.error("Payment captured but activation failed — contact support."); }
                    finally { setBusy(""); }
                },
            });
            rzp.on("payment.failed", () => { toast.error("Payment failed. Please try again."); setBusy(""); });
            rzp.open();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not start checkout"); setBusy(""); }
    };

    const tiers = d?.tiers || [];
    return (
        <div className={`mt-8 ${cls}`} data-testid="social-billing-section">
            <div className="text-center">
                <div className="inline-flex items-center gap-2 rounded-full bg-brand/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-brand">
                    <ShareNetwork size={13} weight="fill" /> Social · Add-on
                </div>
                <h2 className="mt-2 font-heading text-2xl font-bold tracking-tight">Social plans</h2>
                <p className="mx-auto mt-1 max-w-2xl text-sm text-stone2">Publish, engage & convert on LinkedIn, Facebook and Instagram. A standalone add-on — priced by manager seats + features.{cycle === "yearly" ? " Billed yearly · 2 months free." : " Billed monthly."}{currency === "INR" ? " +18% GST." : ""}</p>
            </div>

            {ent?.active && (
                <div className="mx-auto mt-4 max-w-2xl rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-center text-sm text-emerald-800" data-testid="social-bill-active">
                    {ent.status === "trialing" ? `You're on the free trial${ent.trial_ends_at ? ` — ends ${new Date(ent.trial_ends_at).toLocaleDateString()}` : ""}.`
                        : ent.comped ? "Social is enabled for your workspace by your provider."
                        : `Active${ent.tier ? ` · ${ent.tier} plan` : ""}${ent.current_period_end ? ` · renews ${new Date(ent.current_period_end).toLocaleDateString()}` : ""}.`}
                </div>
            )}

            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4">
                {tiers.map((t) => {
                    const price = cycle === "yearly" ? t.price_yearly : t.price_monthly;
                    const isCurrent = ent?.active && ent?.tier === t.id && !ent?.comped;
                    const featured = t.id === "growth";
                    const isAds = t.id === "pro";
                    return (
                        <div key={t.id} data-testid={`social-bill-tier-${t.id}`} className={`relative flex flex-col rounded-2xl border bg-white p-6 transition-all ${isAds ? "border-ink ring-1 ring-ink/20" : featured ? "border-brand ring-1 ring-brand/30" : "border-stone-200"}`}>
                            {featured && <div className="absolute -top-3 left-6 rounded-full bg-brand px-3 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">Most popular</div>}
                            {isAds && <div className="absolute -top-3 left-6 rounded-full bg-ink px-3 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">Ads included</div>}
                            <h3 className="font-heading text-xl font-bold">{t.name}</h3>
                            <p className="mt-1 text-sm text-stone2">{t.tagline}</p>
                            <div className="mt-3"><span className="font-heading text-3xl font-bold">{t.symbol}{price.toLocaleString()}</span><span className="text-sm text-stone2">/{cycle === "yearly" ? "yr" : "mo"}</span></div>
                            <ul className="mt-4 flex-1 space-y-1.5 text-sm text-stone2">
                                {t.features.map((f, i) => <li key={i} className="flex items-start gap-1.5"><CheckCircle size={15} weight="fill" className="mt-0.5 shrink-0 text-emerald-500" /> {f}</li>)}
                            </ul>
                            <button data-testid={`social-bill-subscribe-${t.id}`} disabled={busy || isCurrent} onClick={() => subscribe(t.id)}
                                className={`mt-5 rounded-lg px-4 py-2.5 text-sm font-bold disabled:opacity-50 ${featured ? "bg-brand text-white hover:bg-brand-hover" : "bg-ink text-white hover:opacity-90"}`}>
                                {isCurrent ? "Current plan" : busy === t.id ? "Opening…" : ent?.active ? "Switch to this" : "Subscribe"}
                            </button>
                        </div>
                    );
                })}
            </div>

            {ent?.trial_available && (
                <div className="mx-auto mt-6 flex max-w-3xl flex-wrap items-center justify-between gap-3 rounded-2xl border border-dashed border-stone-300 bg-white p-5">
                    <div>
                        <div className="font-bold text-ink">Not ready to pay? Try it free.</div>
                        <div className="text-sm text-stone2">{d?.trial_days || 30}-day free trial · {d?.trial_seats || 1} user · no card required.</div>
                    </div>
                    <button data-testid="social-bill-trial" disabled={busy} onClick={startTrial}
                        className="rounded-lg border border-brand px-4 py-2.5 text-sm font-bold text-brand hover:bg-brand/5 disabled:opacity-50">{busy === "trial" ? "Starting…" : "Start free trial"}</button>
                </div>
            )}
        </div>
    );
}
